import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import PageLayout from '@/components/feature/PageLayout';
import Careers from '@/pages/home/components/Careers';

const generateJobPostingsSchema = () =>
  JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Job Openings at Thoughtic Solutions",
    "numberOfItems": 4,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "JobPosting",
          "title": "Senior Full Stack Developer",
          "description": "Lead end-to-end development for client projects using modern frameworks. Mentor junior developers and architect scalable solutions.",
          "employmentType": "FULL_TIME",
          "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": "Remote", "addressCountry": "Global" } },
          "hiringOrganization": { "@type": "Organization", "name": "Thoughtic Solutions", "sameAs": "https://thoughtic.com" },
          "datePosted": "2026-06-01",
          "skills": "React, Node.js, TypeScript, PostgreSQL, AWS",
          "industry": "Information Technology"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "JobPosting",
          "title": "DevOps Engineer",
          "description": "Build and maintain CI/CD pipelines, manage cloud infrastructure, and ensure high availability across client environments.",
          "employmentType": "FULL_TIME",
          "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": "Remote", "addressCountry": "Global" } },
          "hiringOrganization": { "@type": "Organization", "name": "Thoughtic Solutions", "sameAs": "https://thoughtic.com" },
          "datePosted": "2026-06-01",
          "skills": "Kubernetes, Terraform, Docker, Azure, GitHub Actions",
          "industry": "Information Technology"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "JobPosting",
          "title": "Mobile App Developer (React Native)",
          "description": "Develop cross-platform mobile applications for iOS and Android. Collaborate with UX designers to deliver polished user experiences.",
          "employmentType": "CONTRACTOR",
          "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": "Remote", "addressCountry": "Global" } },
          "hiringOrganization": { "@type": "Organization", "name": "Thoughtic Solutions", "sameAs": "https://thoughtic.com" },
          "datePosted": "2026-06-01",
          "skills": "React Native, iOS, Android, Firebase, REST APIs",
          "industry": "Information Technology"
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "JobPosting",
          "title": "Technical Recruiter",
          "description": "Source and vet top-tier engineering talent globally. Partner with hiring managers to understand technical requirements and culture fit.",
          "employmentType": "FULL_TIME",
          "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": "Remote", "addressCountry": "Global" } },
          "hiringOrganization": { "@type": "Organization", "name": "Thoughtic Solutions", "sameAs": "https://thoughtic.com" },
          "datePosted": "2026-06-01",
          "skills": "Sourcing, Technical Screening, ATS, LinkedIn Recruiter, Communication",
          "industry": "Information Technology"
        }
      }
    ]
  });

function PageHero({ title, subtitle }: { title: string; subtitle: string }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-12 md:pt-32 md:pb-16 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
            <i className="ri-award-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Join Us</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4">
            {title}
          </h1>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default function CareersPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateJobPostingsSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout title="Careers" description="Join Thoughtic Solutions. Work on meaningful projects with global teams. Open roles for engineers, recruiters, and operations.">
      <PageHero
        title="Careers"
        subtitle="Join a network of top-tier engineers working on meaningful projects with global teams."
      />
      <Careers />
    </PageLayout>
  );
}